import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaHeart, FaPaperPlane } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { listComments, postComment, likeComment, postReply } from '../services/commentsApi';

const CommentSection = ({ recipeId }) => {
  const { user, isAuthenticated } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');

  useEffect(() => {
    (async () => {
      const recipeComments = await listComments(recipeId);
      setComments(recipeComments);
    })();
  }, [recipeId]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      alert('Please log in to comment');
      return;
    }

    if (!newComment.trim()) return;

    setIsSubmitting(true);

    const created = await postComment(recipeId, { author: { name: user.username, avatar: user.avatar }, content: newComment.trim() });
    setComments(prev => [created, ...prev]);
    setNewComment('');
    setIsSubmitting(false);
  };

  const handleLikeComment = async (commentId) => {
    if (!isAuthenticated) {
      alert('Please log in to like comments');
      return;
    }

    setComments(prev => prev.map(comment => (comment.id === commentId ? { ...comment, isLiked: !comment.isLiked, likes: comment.isLiked ? comment.likes - 1 : comment.likes + 1 } : comment)));
    try { await likeComment(recipeId, commentId, true); } catch (e) { /* ignore fallback */ }
  };

  const handleReply = (commentId) => {
    setReplyingTo(commentId);
    setReplyText('');
  };

  const handleSubmitReply = async (parentId) => {
    if (!isAuthenticated || !replyText.trim()) return;

    const created = await postReply(recipeId, parentId, { author: { name: user.username, avatar: user.avatar }, content: replyText.trim() });
    setComments(prev => [created, ...prev]);
    setReplyingTo(null);
    setReplyText('');
  };

  // Threaded comment item (recursive)
  const CommentItem = ({ comment, depth = 0, index = 0 }) => {
    const children = comments.filter((c) => c.parentId === comment.id);
    const [collapsed, setCollapsed] = useState(true);
    const inset = depth > 0;
    return (
      <motion.div
        key={comment.id}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: Math.min(index * 0.05, 0.3) }}
        style={{ borderBottom: depth === 0 ? '1px solid #f3f4f6' : 'none', paddingBottom: 16, marginLeft: inset ? 12 : 0, borderLeft: inset ? '2px solid #e5e7eb' : 'none', paddingLeft: inset ? 12 : 0 }}
      >
        <div style={{ display: 'flex', gap: 12 }}>
          <img src={comment.author.avatar} alt={comment.author.name} style={{ width: 40, height: 40, borderRadius: 999, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <h4 style={{ margin: 0, fontWeight: 700, color: '#111827' }}>{comment.author.name}</h4>
              <span style={{ fontSize: 12, color: '#6b7280' }}>{comment.timestamp}</span>
            </div>
            <p style={{ color: '#374151', margin: '0 0 8px', lineHeight: 1.6 }}>{comment.content}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button onClick={() => handleLikeComment(comment.id)} className="btn" style={{ background: 'transparent', color: comment.isLiked ? '#b91c1c' : '#6b7280', padding: 0, gap: 6, display: 'inline-flex', alignItems: 'center' }}>
                <FaHeart /> <span style={{ fontSize: 14, fontWeight: 600 }}>{comment.likes}</span>
              </button>
              <button onClick={() => handleReply(comment.id)} className="btn" style={{ background: 'transparent', color: '#6b7280', padding: 0 }}>Reply</button>
            </div>

            {replyingTo === comment.id && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} style={{ marginTop: 10, marginLeft: 12, borderLeft: '2px solid #e5e7eb', paddingLeft: 12 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <img src={user?.avatar} alt={user?.username} style={{ width: 32, height: 32, borderRadius: 999, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder={`Reply to ${comment.author.name}...`} className="textarea" rows={2} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button onClick={() => handleSubmitReply(comment.id)} disabled={!replyText.trim()} className="btn btn-green">Reply</button>
                      <button onClick={() => setReplyingTo(null)} className="btn btn-settings">Cancel</button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {children.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <button className="collapse-toggle" onClick={() => setCollapsed(!collapsed)}>
                  {collapsed ? `View ${children.length} repl${children.length>1?'ies':'y'}` : 'Hide replies'}
                </button>
                {!collapsed && (
                  <div style={{ display: 'grid', gap: 12, marginTop: 8 }}>
                    {children.map((child, i) => (
                      <CommentItem key={child.id} comment={child} depth={depth + 1} index={i} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  };


  return (
    <div id="comments" className="card">
      <h3 style={{ marginTop: 0 }}>Comments ({comments.length})</h3>

      {/* Add Comment Form */}
      {isAuthenticated ? (
        <form onSubmit={handleSubmitComment} style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <img src={user?.avatar} alt={user?.username} style={{ width: 40, height: 40, borderRadius: 999, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Share your thoughts about this recipe..."
                className="textarea"
                rows={3}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                <span style={{ fontSize: 12, color: '#6b7280' }}>{newComment.length}/500 characters</span>
                <button type="submit" disabled={!newComment.trim() || isSubmitting} className="btn btn-cta" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  {isSubmitting ? (
                    <div className="animate-spin" style={{ width: 16, height: 16, borderRadius: '999px', borderBottom: '2px solid #fff' }}></div>
                  ) : (
                    <FaPaperPlane />
                  )}
                  {isSubmitting ? 'Posting...' : 'Post Comment'}
                </button>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div className="card" style={{ background: '#f9fafb', textAlign: 'center', marginBottom: 16 }}>
          <p style={{ color: '#6b7280', marginBottom: 12 }}>Join the conversation!</p>
          <a href="/login" className="btn btn-green">Log in to comment</a>
        </div>
      )}

      {/* Comments List */}
      <div style={{ display: 'grid', gap: 16 }}>
        <AnimatePresence>
          {comments.filter(c => !c.parentId).map((comment, index) => (
            <CommentItem key={comment.id} comment={comment} depth={0} index={index} />
          ))}
        </AnimatePresence>

        {comments.length === 0 && (
          <div className="text-center" style={{ padding: '24px 0' }}>
            <p style={{ color: '#6b7280' }}>No comments yet. Be the first to share your thoughts!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentSection;
